import React, { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ShieldAlert } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import useAccess from '../../hooks/useAccess';

/**
 * <ProtectedRoute module="billing" action="create">   (old style)
 * <ProtectedRoute do="billing.create">                 (new style)
 * <ProtectedRoute any={['reports.view', 'inventory.view_analytics']}>
 * No module = only login needed.
 * While the user's access is still loading it shows "Loading", never "no access".
 */
const box = { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '80vh', textAlign: 'center', padding: 20, fontFamily: 'Nunito, sans-serif' };
const btn = { height: 34, padding: '0 16px', border: 'none', borderRadius: 8, background: '#673de6', color: '#fff', fontWeight: 800, fontFamily: 'inherit', cursor: 'pointer' };

const Loading = ({ text = 'Loading...' }) => (
  <div style={box}>
    <div style={{ border: '3px solid #e9e9ec', borderTopColor: '#673de6', borderRadius: '50%', width: 28, height: 28, animation: 'spin 1s linear infinite' }} />
    <p style={{ marginTop: 14, color: '#797980', fontWeight: 700, fontSize: 12 }}>{text}</p>
  </div>
);

const ProtectedRoute = ({ children, module, action = 'view', do: key, any }) => {
  const { user, loading, logout, refreshAccess } = useAuth();
  const { can, canAny, ready } = useAccess();
  const location = useLocation();
  const navigate = useNavigate();
  const [slow, setSlow] = useState(false);

  // access not known after 8 s: offer a way out instead of spinning forever
  useEffect(() => {
    if (ready) { setSlow(false); return undefined; }
    const id = setTimeout(() => setSlow(true), 8000);
    return () => clearTimeout(id);
  }, [ready]);

  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;

  const needKey = key || (module ? `${module}.${action}` : null);
  if (!needKey && !any) return children;            // login only

  if (!ready) {
    if (!slow) return <Loading text="Loading your access..." />;
    return (
      <div style={box}>
        <h1 style={{ color: '#18181a', fontSize: 18, margin: '0 0 8px', fontWeight: 900 }}>Your access could not be loaded</h1>
        <p style={{ color: '#797980', fontSize: 13, maxWidth: 420, lineHeight: 1.6, margin: '0 0 16px', fontWeight: 600 }}>
          Check the internet connection. If it keeps happening, log in again.
        </p>
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="button" style={{ ...btn, background: '#fff', color: '#36363b', border: '1px solid #e9e9ec' }} onClick={() => { setSlow(false); refreshAccess?.(); }}>Try again</button>
          <button type="button" style={btn} onClick={logout}>Log in again</button>
        </div>
      </div>
    );
  }

  const allowed = (!needKey || can(needKey)) && (!any || canAny(any));
  if (allowed) return children;

  return (
    <div style={box}>
      <ShieldAlert size={56} color="#be1025" style={{ marginBottom: 16 }} />
      <h1 style={{ color: '#18181a', fontSize: 22, margin: '0 0 8px', fontWeight: 900 }}>You do not have access to this page</h1>
      <p style={{ color: '#797980', fontSize: 13, maxWidth: 420, lineHeight: 1.6, margin: '0 0 18px', fontWeight: 600 }}>
        Ask your manager or HQ to give you access. If it was just given, it applies in a moment.
      </p>
      <button type="button" onClick={() => navigate('/', { replace: true })} style={btn}>Go to dashboard</button>
    </div>
  );
};

export default ProtectedRoute;